// src/utils/dateUtils.ts
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';

export const formatSafeDate = (date: Date | Timestamp | undefined | null, formatString: string = 'MM/dd/yyyy'): string => {
  if (!date) return 'N/A';
  
  try {
    // Handle Firestore Timestamp
    if (date instanceof Timestamp) {
      return format(date.toDate(), formatString);
    }
    
    // Handle Date object
    if (date instanceof Date) {
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return format(date, formatString);
    }
    
    // Handle string dates
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return 'Invalid Date';
      }
      return format(parsedDate, formatString);
    }
    
    return 'N/A';
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'Invalid Date';
  }
};

// Common US timezones for project settings
export const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST - no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
];

/**
 * Get timezone abbreviation from IANA timezone string.
 * e.g. "America/Phoenix" → "MST", "America/New_York" → "EST" or "EDT"
 */
export const getTimezoneAbbreviation = (ianaTimezone: string): string => {
  if (!ianaTimezone) return '';
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value || '';
  } catch {
    return '';
  }
};

/**
 * Convert 24-hour military time string to 12-hour AM/PM format.
 * e.g. "14:00" → "2:00 PM", "09:00" → "9:00 AM"
 * Optionally appends timezone abbreviation: "2:00 PM MST"
 */
export const formatTime = (militaryTime: string, timezone?: string): string => {
  if (!militaryTime) return '';
  const parts = militaryTime.split(':');
  if (parts.length < 2) return militaryTime;

  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  if (isNaN(hours)) return militaryTime;

  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  const formatted = `${hours}:${minutes} ${period}`;
  if (timezone) {
    const abbr = getTimezoneAbbreviation(timezone);
    return abbr ? `${formatted} ${abbr}` : formatted;
  }
  return formatted;
};

/**
 * Format a time range from two military time strings.
 * e.g. ("09:00", "10:00", "America/Phoenix") → "9:00 AM - 10:00 AM MST"
 * Timezone abbreviation only appended once at the end.
 */
export const formatTimeRange = (startTime: string, endTime: string, timezone?: string): string => {
  if (!startTime || !endTime) return `${startTime || ''} - ${endTime || ''}`;

  const startFormatted = formatTime(startTime);
  const endFormatted = formatTime(endTime);

  const range = `${startFormatted} - ${endFormatted}`;
  if (timezone) {
    const abbr = getTimezoneAbbreviation(timezone);
    return abbr ? `${range} ${abbr}` : range;
  }
  return range;
};

/**
 * Count business days (Mon-Fri) between two dates, excluding start date.
 */
export const countBusinessDaysBetween = (startDate: Date, endDate: Date): number => {
  let count = 0;
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  while (current < end) {
    current.setDate(current.getDate() + 1);
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
  }
  return count;
};

/**
 * Add N business days to a date and return the resulting date.
 */
export const addBusinessDays = (referenceDate: Date, businessDays: number): Date => {
  const result = new Date(referenceDate);
  let remaining = businessDays;
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    const dayOfWeek = result.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      remaining--;
    }
  }
  return result;
};

/**
 * Calculate business days remaining before a lead's auto-validation deadline.
 * Returns days remaining, the deadline date, and whether it's overdue.
 */
export const getBusinessDaysRemaining = (
  createdAt: Date,
  deadlineDays: number
): { daysRemaining: number; deadlineDate: Date; isOverdue: boolean } => {
  const now = new Date();
  const elapsed = countBusinessDaysBetween(createdAt, now);
  const daysRemaining = deadlineDays - elapsed;
  const deadlineDate = addBusinessDays(createdAt, deadlineDays);
  return {
    daysRemaining,
    deadlineDate,
    isOverdue: daysRemaining <= 0,
  };
};

export const toDateObject = (date: any): Date | null => {
  if (!date) return null;
  
  if (date instanceof Timestamp) {
    return date.toDate();
  }
  
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }
  
  if (typeof date === 'string') {
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  
  return null;
};